import { IElement, OneOfElements } from '@shared/models/project.model';
import { projectGuards } from '../../guards/project-guards.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { orderElementPropertiesLikeCategoryTemplate } from '../../element-category.helpers';

type CategoryEnrichChange =
  ProjectChangeMap[ProjectChangeType.ElementCategoryEnrich];

/**
 * Build a partial element change for ElementCategoryEnrich.
 * Merges add/remove of properties and children from category definition.
 *
 * @param element - Current element state
 * @param change - Category enrich payload
 * @returns Partial with properties and elements, or undefined if not an element.
 *   Identity enrichments are dropped in `projectChangeToElementPartial`.
 */
export const elementCategoryEnrichToChange = (
  element: OneOfElements,
  change: CategoryEnrichChange,
): Partial<IElement> | undefined => {
  if (!projectGuards.is.element(element)) return undefined;

  const currentProps = element.properties ?? [];
  const currentElements = element.elements ?? [];

  // An added child replaces any kept child with the same id; legacy enrich payloads
  // re-add slots without listing them in removeChildIds, which would duplicate ids.
  const replacedIds = new Set([
    ...change.removeChildIds,
    ...change.addChildren.map((child) => child.id),
  ]);
  const elements = currentElements
    .filter((c) => !replacedIds.has(c.id))
    .concat(change.addChildren);

  const mergedProps = currentProps
    .filter((p) => !change.removePropertyNames.includes(p.name))
    .concat(change.addProperties);

  // Reorder according to category template so UI / persisted layout matches the canonical order.
  const properties = orderElementPropertiesLikeCategoryTemplate({
    ...element,
    properties: mergedProps,
    elements,
  });

  return { elements, properties };
};
